<template>

  <div data-testid="my-calendar.meeting-request.toggle-list-btn" class="flex items-center cursor-pointer justify-between px-2 py-2.5 mt-8 text-[#999999] w-[300px]" @click="showMeetingRequestList = !showMeetingRequestList">
    <span data-testid="my-calendar.meeting-request.list-title-text" class="text-[#666666] font-semibold">Patient Meeting Request ({{ meetingRequestList.length }})</span>
    <img
      v-if="meetingRequestList.length > 0"
      :class="['cursor-pointer w-3 h-3', { 'rotate-180': showMeetingRequestList }]"
      src="@/assets/my-calendar/arrow-down.svg" 
      alt="arrow-down" 
      />
  </div>
  <div v-if="showMeetingRequestList" class="w-[300px]">
    <InlineNotification
      v-if="canceledMettingRequestList.length > 0"
      data-testid="my-calendar.meeting-request.canceled-slots-notification"
      theme="info"
      class="meeting-request-alert"
    >
      <template #default>
        {{ canceledMettingRequestList.length }} time slots available due to patient cancellation
        <span
          data-testid="my-calendar.meeting-request.toggle-canceled-detail-btn"
          class="text-[#1E4670] font-semibold cursor-pointer underline ml-1"
          @click="showDetail = !showDetail"
        >
          {{ showDetail ? 'See Less' : 'See Detail' }}
        </span>
      </template>
    </InlineNotification>

    <transition name="fade-detail">
      <div v-if="showDetail" class="detail-panel">
        <div
          v-for="meetingRequest in canceledMettingRequestList"
          :key="meetingRequest.id"
          :data-testid="`my-calendar.meeting-request.canceled-slot.${meetingRequest.id}`"
          class="popover-option"
        >
          {{ formatMeetingRequestTime(meetingRequest) }}
        </div>
      </div>
    </transition>

    <transition-group name="fade-card" tag="div" class="flex flex-col gap-3 mt-3">
      <MeetingRequestCard
        v-for="item in meetingRequestList"
        :key="item.id"
        :meeting-request-info="item"
        :action-state="meetingRequestActionMap[item.id]"
        @remove="removeMeeting(item.id)"
        @action-start="handleActionStart(item.id, $event)"
        @action-success="handleActionSuccess(item.id, $event)"
        @open-meeting-card="openMeetingCard(item)"
        @open-schedule-dialog="openScheduleDialog"
      />
    </transition-group>

  </div>

  <div v-if="meetingRequestList.length === 0" data-testid="my-calendar.meeting-request.empty-state" class="empty-block">
    <div>
      <img src="@/assets/my-calendar/meeting-request/empty.png" alt="empty-meeting-request" />
    </div>
    <div class="empty-text">
      You have no meeting requests.
    </div>
  </div>

  <MeetingCard
    :visible="showMeetingCard"
    :meeting="currentMeeting"
    :action-state="currentMeeting ? meetingRequestActionMap[currentMeeting.id] : null"
    @remove="removeMeeting(currentMeeting.id)"
    @action-start="handleActionStart(currentMeeting.id, $event)"
    @action-success="handleActionSuccess(currentMeeting.id, $event)"
    @open-schedule-dialog="openScheduleDialog"
    @close="showMeetingCard = false"
  />

  <ScheduleDetail
    :visible="scheduleDetailVisible"
    :active-tab="'appointment'"
    :hide-switch-tab="true"
    @close="closeScheduleDetail"
  />

  

</template>

<script setup>
import { ref,computed,watch } from 'vue'
import MeetingRequestCard from './MeetingRequestCard.vue'
import MeetingCard from './MeetingCard.vue'
import ScheduleDetail from './schedule/ScheduleDetail.vue'
import moment from 'moment-timezone'
import { useStore } from 'vuex'

const emit = defineEmits(['open-schedule-dialog'])
const showMeetingRequestList = ref(true)
const showDetail = ref(false)
const showMeetingCard = ref(false)
const currentMeeting = ref(null)

const store = useStore()
const rawMeetingRequestList = computed(() => store.state.calendar.rawMeetingRequestList)
const meetingRequestList = ref([])
const meetingRequestActionMap = ref({})
const canceledMettingRequestList = computed(() => store.state.calendar.canceledMettingRequestList)
const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])

const scheduleDetailVisible = ref(false)
const closeScheduleDetail = () => { 
  store.dispatch('calendar/setCurrentEventInfo', null)
  scheduleDetailVisible.value = false 
}


function openMeetingCard(item) {
  currentMeeting.value = item
  showMeetingCard.value = true
}

function syncMeetingRequestList() {
  const previousItems = meetingRequestList.value || []
  const rawItems = rawMeetingRequestList.value || []
  const rawItemMap = new Map(rawItems.map(item => [item.id, item]))
  const rawIds = new Set(rawItems.map(item => item.id))
  const nextItems = []

  previousItems.forEach((item) => {
    if (rawItemMap.has(item.id)) {
      nextItems.push(rawItemMap.get(item.id))
      rawItemMap.delete(item.id)
      return
    }

    if (meetingRequestActionMap.value[item.id] && !rawIds.has(item.id)) {
      nextItems.push(item)
    }
  })

  rawItemMap.forEach((item) => {
    nextItems.push(item)
  })

  meetingRequestList.value = nextItems

  if (currentMeeting.value) {
    const nextCurrentMeeting = meetingRequestList.value.find(item => item.id === currentMeeting.value.id)
    if (nextCurrentMeeting) {
      currentMeeting.value = nextCurrentMeeting
    }
  }
}

function openScheduleDialog(info) {
  // console.log(info,'openScheduleDialog13231321')
  let participants = [];
  if (info.patient_calendar_id) {
    participants.push({
      participant_calendar_id: info.patient_calendar_id,
      role: 'patient'
    });
  }
  if (info.provider?.customer_id) {
    participants.push({
      participant_calendar_id: info.provider_calendar_id,      
      role: 'provider'
    });
  }
  let eventInfo={
    start: info.requested_start_time,
    end: info.requested_end_time,
    title: info.event_title,
    event_type: info.event_type,
    patient_calendar_id: info.patient_calendar_id,
    practice_id: info.clinic.clinic_id,
    participants: participants,
    uuid: info.uuid,
    mode: 'reschedule'
  }
  store.dispatch('calendar/setCurrentEventInfo', eventInfo)
  scheduleDetailVisible.value = true
}
function removeMeeting(id) {
  meetingRequestList.value = meetingRequestList.value.filter(item => item.id !== id)
  delete meetingRequestActionMap.value[id]
  meetingRequestActionMap.value = { ...meetingRequestActionMap.value }

  if (currentMeeting.value?.id === id) {
    currentMeeting.value = null
    showMeetingCard.value = false
  }
}

function handleActionStart(id, action) {
  meetingRequestActionMap.value = {
    ...meetingRequestActionMap.value,
    [id]: {
      phase: 'loading',
      action
    }
  }
}

function handleActionSuccess(id, action) {
  meetingRequestActionMap.value = {
    ...meetingRequestActionMap.value,
    [id]: {
      phase: 'success',
      action
    }
  }
}
function formatMeetingRequestTime(meetingRequest) {
  const datePart = moment.utc(meetingRequest.requested_start_time).tz(calendarTimezone.value).format('MMM D, YYYY')
  const startTime = moment.utc(meetingRequest.requested_start_time).tz(calendarTimezone.value).format('h:mmA')
  const endTime = moment.utc(meetingRequest.requested_end_time).tz(calendarTimezone.value).format('h:mmA')
  return `${datePart} ${startTime} - ${endTime}`;
}


watch(rawMeetingRequestList, () => {
  // console.log(rawMeetingRequestList.value,'rawMeetingRequestListadad')
  syncMeetingRequestList()
}, { immediate: true })

</script>
<style scoped>

.popover-option {
  display: flex;
  justify-content: center;
  padding: 4px 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  white-space: pre;
}
.detail-panel {
  background: #fff;
  border-radius: 0 0 4px 4px;
  margin-top: 12px;
  padding: 6px;
}
.empty-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #FFFFFF;
  border-radius: 5px;
  box-shadow: 0px 1px 10px 0px rgba(74, 74, 74, 0.08);
  padding: 12px;
  gap: 12px;
}
.empty-text {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #666;
  text-align: center;
}
/* Transtion css */
.fade-detail-enter-active, 
.fade-detail-leave-active  {
  transition: opacity 0.3s ease;
}
.fade-detail-leave-to,
.fade-card-leave-to{
  opacity: 0;
}
.fade-card-leave-active  {
  transition: opacity 0.5s ease-out;
  position: absolute;
  width: 300px;
}
.fade-card-move {
  transition: transform 1s cubic-bezier(0.55, 0, 0.1, 1);
}

</style>

